import { defineStore } from 'pinia';
import { UsersService } from '~/units/users/services';
import type { User, UserBasicInfo, UsersMap } from '~/units/users/types';

let fetchUsersPromise: Promise<UsersMap> | null = null;

export const useUsersStore = defineStore('users', {
  state: (): {
    users: User[];
    usersMap: UsersMap;
    isFetched: boolean;
  } => {
    return {
      users: [],
      usersMap: {},
      isFetched: false,
    };
  },

  actions: {
    async fetchUsers() {
      if (this.isFetched) {
        return this.usersMap;
      }

      if (!fetchUsersPromise) {
        fetchUsersPromise = (async () => {
          try {
            const usersService = new UsersService();
            const response = await usersService.fetchUsers();

            if (response && response.data) {
              this.users = response.data;
              this.generateUsersMap();
              this.isFetched = true;
            }

            return this.usersMap;
          } catch (e) {
            console.error('Error fetching users:', e);
            return {};
          } finally {
            fetchUsersPromise = null;
          }
        })();
      }

      return fetchUsersPromise;
    },

    generateUsersMap() {
      const usersMap: UsersMap = {};
      
      this.users.forEach((user: User) => {
        const userBasicInfo: UserBasicInfo = {
          user_id: user.user_id,
          name: user.name,
          surname: user.surname,
          email: user.email,
          image: user.image,
          job_title: user.job_title,
          fullname: user.fullname,
          phone: user.phone,
        };
        
        usersMap[user.user_id] = userBasicInfo;
      });
      
      this.usersMap = usersMap;
    },

    getUserById(userId: number): UserBasicInfo | null {
      if (!this.isFetched) {
        return null;
      }
      return this.usersMap[userId] || null;
    },

    getUsersByIds(userIds: number[]): { [userId: number]: UserBasicInfo | null } {
      const result: { [userId: number]: UserBasicInfo | null } = {};
      
      userIds.forEach((userId) => {
        result[userId] = this.getUserById(userId);
      });
      
      return result;
    },
  },

  getters: {
    getUsersMap: (state): UsersMap => state.usersMap,
    isUsersFetched: (state): boolean => state.isFetched,
  },
}); 